import { useMemo, useState } from 'react';
import type { GuideResponse, WorkshopState } from '../../api/types';
import {
  type DeepDiveBand,
  type DeepDivePhase,
  type DeepDiveQuestion,
  type DeepDiveScenario,
  type DeepDiveSection,
  type StakeholderId,
} from '../../config/deepDive';
import {
  resolveAssetForStep,
  resolveFileLabelForStep,
  resolveIqScenarios,
  resolvePlayById,
  resolvePushback,
} from '../../api/resolveContent';
import { getCampaignAssetDownload } from '../../../../../support/campaignAssets';
import {
  BAND_OF,
  deepDiveAnalysis,
  whoText,
  type DeepDiveAnalysis,
} from '../../modules/guide/deepDiveAnalysis';
import { useWorkshopStore } from '../../store/workshopStore';
import { cx, ui } from '../../components/ui/primitives';
import { BlurTextarea } from '../../components/ui/BlurTextarea';
import { GuideFindings } from './GuideFindings';
import { GuideText } from './GuideText';
import s from './guide.module.css';

/** Stored band values, paired with the guide's own names for them. */
const BANDS: { key: 'red' | 'amber' | 'green'; band: DeepDiveBand; label: string }[] = [
  { key: 'red', band: 'gap', label: 'GAP' },
  { key: 'amber', band: 'partial', label: 'PARTIAL' },
  { key: 'green', band: 'covered', label: 'COVERED' },
];

const GAP_VIEW = 'gap-analysis';

/**
 * The deep-dive, run live with the room.
 *
 * Laid out the way the Scenario Console lays it out, because that shape was
 * designed for someone holding a conversation rather than filling in a form:
 *
 *   - One section at a time. Ninety-one questions in a single column reads as
 *     an interrogation; a rail of five or six named sections reads as a plan,
 *     and lets the seller skip the ones this room cannot answer.
 *   - Every phrase on screen at once. The seller does not decide "this is a
 *     gap" and then look for evidence. They listen, hear something close to one
 *     of the phrases, and tap it, and the verdict follows from that.
 *   - What the answer unlocks, under the answer. A play named without its
 *     description is a code word.
 *
 * The scenario itself is settled before the room is booked: Profiling checked
 * the entitlement and the proposal call confirmed how it is used, so it is
 * stated here rather than asked again.
 */
export function DeepDivePanel({
  state,
  presenting,
  customerName = 'this account',
  seName = 'Your Cisco account team',
}: {
  state: WorkshopState;
  presenting?: boolean;
  customerName?: string;
  seName?: string;
}) {
  const [who, setWho] = useState<StakeholderId | null>(null);
  const [phaseId, setPhaseId] = useState<DeepDivePhase>('assess');
  const [view, setView] = useState<string>('');
  const [openPushback, setOpenPushback] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cheatBusy, setCheatBusy] = useState(false);

  // The scenario cheat sheet is a managed campaign asset (S3), resolved from the Deep-Dive step's
  // `cheatsheet` file slot. There is no bundled fallback: the download appears only once the content
  // team has uploaded the file (i.e. the asset has an uploaded object key).
  const cheatSheetAsset = resolveAssetForStep('deepdive', 'cheatsheet');
  const cheatSheetLabel = resolveFileLabelForStep('deepdive', 'cheatsheet');

  const analysis = deepDiveAnalysis(state);
  const scenario = analysis.scenario;
  const pushback = resolvePushback();
  const phase = scenario?.phases.find((p) => p.id === phaseId) ?? scenario?.phases[0];
  const sections = phase?.sections ?? [];
  const current =
    view === GAP_VIEW ? null : (sections.find((section) => section.id === view) ?? sections[0]);

  // The live scenario's questions, exported as a PDF for the room — the deep-dive equivalent of the
  // discovery cheat sheet, grouped by phase and section in the on-screen order. Replaces the per-call
  // question picker that used to live on the Propose step.
  async function downloadQuestions() {
    if (!scenario) return;
    setExporting(true);
    try {
      const rows: { category: string; question: string }[] = [];
      for (const ph of scenario.phases) {
        for (const sec of ph.sections) {
          for (const q of sec.questions) {
            rows.push({ category: `${ph.label} — ${sec.title}`, question: q.ask });
          }
        }
      }
      const [{ generateCheatSheetBlob }, { triggerDownload, fileNameFor }] = await Promise.all([
        import('../../modules/export/cheatSheet'),
        import('../../modules/export/download'),
      ]);
      const blob = await generateCheatSheetBlob({ customerName, seName, questions: rows });
      triggerDownload(
        blob,
        fileNameFor(customerName, 'Frontier-AI-Workshop-Deep-Dive-Questions', 'pdf'),
      );
    } finally {
      setExporting(false);
    }
  }

  // Download the managed cheat sheet: a short-lived signed GET of the uploaded S3 object (the backend
  // sets the right extension/attribution). No bundled fallback — the button only renders when an asset
  // is present.
  async function downloadCheatSheet() {
    if (cheatSheetAsset?.id == null) return;
    const filename = cheatSheetLabel || 'Frontier-AI-Workshop-Scenario-Cheat-Sheet.pdf';
    setCheatBusy(true);
    try {
      const { url } = await getCampaignAssetDownload(cheatSheetAsset.id, filename);
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } finally {
      setCheatBusy(false);
    }
  }

  if (!scenario || !phase) {
    return (
      <p className={s.setupNote}>
        Pick where this customer stands with Cisco IQ to load the right set of questions. It is
        normally answered on the proposal call, under the recommended questions.
      </p>
    );
  }

  return (
    <div className={s.guide}>
      <div className={s.controls}>
        <p className={s.scenarioLine}>
          <span className={s.scenarioLabel}>Cisco IQ</span>
          {resolveIqScenarios().find((iq) => iq.id === scenario.id)?.label ?? scenario.title}
          <span className={s.scenarioCount}>{countQuestions(scenario)} questions</span>
          {!presenting && (
            <>
              <button
                type="button"
                className={s.cheatSheet}
                onClick={() => void downloadQuestions()}
                disabled={exporting}
              >
                {exporting ? 'Building…' : 'Download questions (PDF)'}
              </button>
              {cheatSheetAsset && (
                <button
                  type="button"
                  className={s.cheatSheet}
                  onClick={() => void downloadCheatSheet()}
                  disabled={cheatBusy}
                >
                  {cheatBusy ? 'Preparing…' : cheatSheetLabel || 'Download the cheat sheet'}
                </button>
              )}
            </>
          )}
        </p>

        <p className={s.setupNote}>
          <b>Running this session.</b> <GuideText text={scenario.setup} />
        </p>

        <div className={s.phaseTabs} role="tablist" aria-label="Phase of the conversation">
          {scenario.phases.map((option) => (
            <button
              key={option.id}
              role="tab"
              type="button"
              aria-selected={option.id === phase.id}
              className={cx(s.phaseTab, option.id === phase.id && s.phaseTabOn)}
              onClick={() => {
                setPhaseId(option.id);
                setView('');
              }}
            >
              {option.n} {option.label}
              <span>{option.sections.reduce((n, sec) => n + sec.questions.length, 0)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={s.deskLayout}>
        <SectionRail
          phaseLabel={phase.label}
          sections={sections}
          analysis={analysis}
          state={state}
          who={who}
          active={view === GAP_VIEW ? GAP_VIEW : (current?.id ?? '')}
          onPick={setView}
          onWho={setWho}
          presenting={presenting}
        />

        <div className={s.desk}>
          {view === GAP_VIEW ? (
            <>
              <p className={s.deskLead}>
                <b>Gap analysis.</b> Built from what you logged. A gap counts three, a partial
                counts one, and anything already covered adds nothing.
              </p>
              <GuideFindings analysis={analysis} presenting={presenting} phase={phase.id} />
            </>
          ) : current ? (
            <SectionBody
              section={current}
              state={state}
              who={who}
              presenting={presenting}
              phaseLead={phase.lead}
            />
          ) : null}
        </div>
      </div>

      {/* The pivots for the deflections that come up in most sessions. Collapsed
          by default: it is there when a conversation stalls, not on screen the
          whole time. */}
      <section className={s.lane}>
        <button
          type="button"
          className={s.laneHead}
          onClick={() => setOpenPushback((open) => !open)}
          aria-expanded={openPushback}
        >
          <span className={s.laneNum}>&#8801;</span>
          <span className={s.laneTitle}>
            <b>Handling pushback</b>
            <span>Quick pivots for common deflections</span>
          </span>
          <span className={s.laneCount}>{pushback.length}</span>
        </button>
        {openPushback && (
          <div className={s.laneBody}>
            {pushback.map((entry) => (
              <div key={entry.objection} className={s.hearRow}>
                <p className={s.hearSay}>&ldquo;{entry.objection}&rdquo;</p>
                <p className={s.hearReply}>
                  <GuideText text={entry.response} />
                </p>
                <p className={s.hearLeads}>{entry.why}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function countQuestions(scenario: DeepDiveScenario) {
  return scenario.phases.reduce(
    (n, phase) => n + phase.sections.reduce((m, section) => m + section.questions.length, 0),
    0,
  );
}

function isLogged(response: GuideResponse | undefined): boolean {
  return !!response?.band || !!response?.noAnswer;
}

/**
 * Where you are, what is left, and who is in the room.
 *
 * The counts are per section so the seller can see at a glance which subjects
 * this room actually answered, and jump past the ones it cannot.
 */
function SectionRail({
  phaseLabel,
  sections,
  analysis,
  state,
  who,
  active,
  onPick,
  onWho,
  presenting,
}: {
  phaseLabel: string;
  sections: DeepDiveSection[];
  analysis: DeepDiveAnalysis;
  state: WorkshopState;
  who: StakeholderId | null;
  active: string;
  onPick: (id: string) => void;
  onWho: (who: StakeholderId | null) => void;
  presenting?: boolean;
}) {
  return (
    <nav className={s.rail} aria-label={`${phaseLabel} sections`}>
      <p className={s.railHead}>
        {phaseLabel} &mdash; {sections.length} section{sections.length === 1 ? '' : 's'}
      </p>

      <ol className={s.railList}>
        {sections.map((section, index) => {
          const scoped = section.questions.filter((q) => !who || q.who.includes(who));
          const logged = scoped.filter((q) => isLogged(state.guideResponses[q.id])).length;
          const done = scoped.length > 0 && logged === scoped.length;
          return (
            <li key={section.id}>
              <button
                type="button"
                className={cx(s.railItem, active === section.id && s.railItemOn)}
                onClick={() => onPick(section.id)}
              >
                <span className={s.railNum}>{String(index + 1).padStart(2, '0')}</span>
                <span className={s.railTitle}>{section.short}</span>
                <span className={cx(s.railCount, done && s.railCountDone)}>
                  {logged} of {scoped.length} logged
                </span>
                <span className={s.railBar} aria-hidden="true">
                  <i style={{ width: `${scoped.length ? (logged / scoped.length) * 100 : 0}%` }} />
                </span>
              </button>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            className={cx(s.railItem, active === GAP_VIEW && s.railItemOn)}
            onClick={() => onPick(GAP_VIEW)}
          >
            <span className={s.railNum}>GA</span>
            <span className={s.railTitle}>Gap analysis</span>
            <span className={s.railCount}>
              {analysis.asked} signal{analysis.asked === 1 ? '' : 's'} &middot;{' '}
              {analysis.plays.length} play{analysis.plays.length === 1 ? '' : 's'} ranked
            </span>
          </button>
        </li>
      </ol>

      {!presenting && (
        <>
          <p className={s.railHead}>Filter by who is in the room</p>
          <div className={s.roleRow} role="group" aria-label="Filter by who is in the room">
            <button
              type="button"
              className={cx(s.roleChip, who === null && s.roleChipOn)}
              onClick={() => onWho(null)}
            >
              All questions
              <span className={s.roleChipCount}>{analysis.total}</span>
            </button>
            {analysis.stakeholders.map((entry) => (
              <button
                key={entry.who}
                type="button"
                className={cx(s.roleChip, who === entry.who && s.roleChipOn)}
                onClick={() => onWho(who === entry.who ? null : entry.who)}
              >
                {entry.label}
                <span className={s.roleChipCount}>{entry.total}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </nav>
  );
}

function SectionBody({
  section,
  state,
  who,
  presenting,
  phaseLead,
}: {
  section: DeepDiveSection;
  state: WorkshopState;
  who: StakeholderId | null;
  presenting?: boolean;
  phaseLead: string;
}) {
  const setGuideResponse = useWorkshopStore((st) => st.setGuideResponse);
  const pickGuideCue = useWorkshopStore((st) => st.pickGuideCue);

  const questions = useMemo(
    () => section.questions.filter((question) => !who || question.who.includes(who)),
    [section, who],
  );

  return (
    <>
      <p className={s.deskEyebrow}>
        {section.group} &middot; {phaseLead}
      </p>
      <h4 className={s.deskTitle}>{section.title}</h4>
      <p className={s.deskWhy}>
        <GuideText text={section.why} />
      </p>
      <p className={s.deskCount}>
        Showing {questions.length} of {section.questions.length}
        {who ? ' · filtered to who is in the room' : ''}
      </p>

      {questions.map((question) => (
        <QuestionCard
          key={question.id}
          question={question}
          response={state.guideResponses[question.id]}
          presenting={presenting}
          onChange={(patch) => setGuideResponse(question.id, patch)}
          onCue={(band, cue) => pickGuideCue(question.id, band, cue)}
        />
      ))}
    </>
  );
}

function QuestionCard({
  question,
  response,
  presenting,
  onChange,
  onCue,
}: {
  question: DeepDiveQuestion;
  response: GuideResponse | undefined;
  presenting?: boolean;
  onChange: (patch: Partial<GuideResponse>) => void;
  onCue: (band: 'red' | 'amber' | 'green', cue: string) => void;
}) {
  const [noting, setNoting] = useState(false);
  const band = response?.band;
  const heard = response?.cues ?? [];
  const note = response?.note ?? '';
  const unlocked = band ? question.bands[BAND_OF[band]].plays : [];
  const playById = resolvePlayById();

  return (
    <div className={cx(s.q, band && s[`q_${band}`])}>
      <div className={s.qHead}>
        <span className={s.qId}>Q {question.id}</span>
        <span className={s.qWho}>{whoText(question.who)}</span>
        {!presenting && (
          <button
            type="button"
            className={s.qAction}
            aria-pressed={noting || !!note}
            onClick={() => setNoting((open) => !open)}
          >
            {note ? 'Note added' : 'Add note'}
          </button>
        )}
        <button
          type="button"
          className={cx(s.qAction, response?.noAnswer && s.qActionOn)}
          aria-pressed={!!response?.noAnswer}
          onClick={() => onChange({ noAnswer: !response?.noAnswer })}
        >
          No answer
        </button>
      </div>

      <p className={s.qAsk}>&ldquo;{question.ask}&rdquo;</p>
      {!presenting && question.why && (
        <p className={s.qWhy}>
          <GuideText text={question.why} />
        </p>
      )}

      {/* Every phrase, under every verdict, at once. Tapping one is what records
          the verdict: the seller is listening, not classifying. */}
      <div className={s.bandGrid}>
        {BANDS.map((option) => {
          const on = band === option.key && !response?.noAnswer;
          return (
            <div key={option.key} className={cx(s.bandRow, on && s.bandRowOn)}>
              <button
                type="button"
                className={cx(s.bandLabel, s[`band_${option.key}`], on && s.bandLabelOn)}
                aria-pressed={on}
                onClick={() => onChange({ band: on ? undefined : option.key })}
              >
                <span className={s.bandDot} aria-hidden="true" />
                {option.label}
                {on && <span className={s.bandLogged}>logged</span>}
              </button>
              <div className={s.cues}>
                {question.bands[option.band].cues.map((cue) => {
                  const picked = on && heard.includes(cue);
                  return (
                    <button
                      key={cue}
                      type="button"
                      aria-pressed={picked}
                      className={cx(s.cue, s[`cue_${option.key}`], picked && s.cueOn)}
                      onClick={() => onCue(option.key, cue)}
                    >
                      {picked && <span aria-hidden="true">&#10003; </span>}
                      {cue}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* What that answer opens up. A recommendation named on its own is a code
          word, so the description travels with it. */}
      {unlocked.length > 0 && (
        <details className={s.unlocks} open>
          <summary>
            What this unlocks &mdash; {unlocked.length} recommendation{unlocked.length === 1 ? '' : 's'}
          </summary>
          <dl className={s.unlockList}>
            {unlocked.map((id) => {
              const play = playById.get(id);
              if (!play) return null;
              return (
                <div key={id}>
                  <dt>{play.name}</dt>
                  <dd>
                    <GuideText text={play.detail} />
                  </dd>
                </div>
              );
            })}
          </dl>
        </details>
      )}

      {!presenting && (noting || note) && (
        <BlurTextarea
          className={cx(ui.textarea, s.qNote)}
          placeholder="What they said..."
          aria-label={`Note for ${question.id}`}
          value={note}
          onCommit={(v) => onChange({ note: v })}
        />
      )}
    </div>
  );
}
